import * as THREE from 'three';

/**
 * VR 校准面板（CanvasTexture）。
 *
 * 之前用 three-mesh-ui，字体 200 正常但 Text 没有几何（整块全黑），
 * 改用 three.js 标准做法：2D 画布画字 → CanvasTexture → 平面。
 * 射线命中后用 UV 换算画布坐标，判断落在哪个按钮上。
 *
 * 现在每行校准项都带 [−][+] 步进按钮，校准可以完全在头显里完成。
 */

export interface VrPanelStatus {
  posture: string;
  voice: string;
  net: string;
  mirrorOn: boolean;
  bgmOcean: boolean;
  bgmMusic: boolean;
  roomCode: string;
  voiceOn: boolean;
  scale: number;
  reach: number;
  wristDeg: number;
  tiltDeg: number;
  yawDeg: number;
}

export type CalKey = 'scale' | 'reach' | 'wristDeg' | 'tiltDeg' | 'yawDeg';

export interface VrPanelActions {
  toggleSit(): void;
  toggleMute(): void;
  resetCalibration(): void;
  toggleMirror(): void;
  toggleOcean(): void;
  toggleMusic(): void;
  /** VR 里的快捷消息（没有虚拟键盘的折中方案） */
  /** 打开 DOM Overlay 文字输入（会弹系统键盘） */
  openChat(): void;
  quickChat(text: string): void;
  /** 复制邀请链接（射线点击算用户手势，clipboard 可用） */
  copyInvite(): void;
  /** 语音开关（开麦/关麦） */
  toggleVoice(): void;
  adjustCalibration(key: CalKey, delta: number): void;
}

const CANVAS_W = 512;
/** VR 快捷消息（没有系统虚拟键盘时的折中） */
const QUICK_CHATS = ['你好呀', '看这边！', '太美了吧', '等我一下'];
const CANVAS_H = 1196;

interface Region {
  rect: [number, number, number, number];
  id: string;
  run: () => void;
}

export class VrPanel {
  readonly group = new THREE.Group();
  readonly interactables: THREE.Object3D[] = [];

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly mesh: THREE.Mesh;
  private readonly regions: Region[] = [];

  private status: VrPanelStatus = {
    posture: '坐',
    voice: '关',
    net: '—',
    mirrorOn: false,
    bgmOcean: true,
    bgmMusic: true,
    roomCode: '----',
    voiceOn: false,
    scale: 1.1,
    reach: 1,
    wristDeg: 20,
    tiltDeg: 60,
    yawDeg: 180,
  };
  private hovered = -1;

  constructor(private readonly actions: VrPanelActions) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(0.62, 1.45),
      new THREE.MeshBasicMaterial({ map: this.texture, transparent: true }),
    );
    this.mesh.name = 'vr-status-panel';
    this.group.add(this.mesh);
    this.interactables.push(this.mesh);

    this.group.position.set(0, 1.0, -1.1);
    this.buildRegions();
    this.redraw();
  }

  /** 按钮布局（画布坐标），与 redraw 里的绘制保持一致 */
  private buildRegions(): void {
    const rows: Array<{ key: CalKey; step: number }> = [
      { key: 'scale', step: 0.05 },
      { key: 'reach', step: 0.05 },
      { key: 'wristDeg', step: 15 },
      { key: 'tiltDeg', step: 5 },
      { key: 'yawDeg', step: 15 },
    ];

    rows.forEach((row, i) => {
      const y = 190 + i * 56;
      this.regions.push({
        rect: [368, y, 60, 44],
        id: `${row.key}-minus`,
        run: () => this.actions.adjustCalibration(row.key, -row.step),
      });
      this.regions.push({
        rect: [436, y, 60, 44],
        id: `${row.key}-plus`,
        run: () => this.actions.adjustCalibration(row.key, row.step),
      });
    });

    // 快捷聊天：2 列 x 2 行
    QUICK_CHATS.forEach((text, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      this.regions.push({
        rect: [24 + col * 236, 560 + row * 52, 228, 44],
        id: `chat-${i}`,
        run: () => this.actions.quickChat(text),
      });
    });

    this.regions.push({
      rect: [24, 668, 464, 44],
      id: 'type',
      run: () => this.actions.openChat(),
    });

    this.regions.push({
      rect: [24, 724, 464, 44],
      id: 'invite',
      run: () => this.actions.copyInvite(),
    });

    const bottom = [
      { id: 'sit', label: '', run: () => this.actions.toggleSit() },
      { id: 'mute', label: '', run: () => this.actions.toggleMute() },
      { id: 'ocean', label: '', run: () => this.actions.toggleOcean() },
      { id: 'music', label: '', run: () => this.actions.toggleMusic() },
      { id: 'mirror', label: '', run: () => this.actions.toggleMirror() },
      { id: 'reset', label: '', run: () => this.actions.resetCalibration() },
      { id: 'voice', label: '', run: () => this.actions.toggleVoice() },
    ];
    bottom.forEach((b, i) => {
      this.regions.push({ rect: [24, 780 + i * 52, 464, 44], id: b.id, run: b.run });
    });
  }

  private drawButton(x: number, y: number, w: number, h: number, label: string, index: number): void {
    const c = this.ctx;
    const hover = this.regions[index] === this.regions[this.hovered];
    c.fillStyle = hover ? 'rgba(63, 127, 160, 0.95)' : 'rgba(28, 43, 56, 0.95)';
    c.strokeStyle = hover ? 'rgba(170, 225, 245, 0.95)' : 'rgba(90, 130, 150, 0.6)';
    c.lineWidth = 3;
    c.beginPath();
    c.rect(x, y, w, h);
    c.fill();
    c.stroke();

    c.fillStyle = '#eaf2f8';
    c.font = '26px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillText(label, x + w / 2, y + h / 2 + 1);
  }

  private redraw(): void {
    const c = this.ctx;
    const s = this.status;

    c.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // 背板
    c.fillStyle = 'rgba(14, 22, 32, 0.93)';
    c.strokeStyle = 'rgba(120, 200, 230, 0.55)';
    c.lineWidth = 4;
    c.beginPath();
    c.rect(4, 4, CANVAS_W - 8, CANVAS_H - 8);
    c.fill();
    c.stroke();

    // 标题
    c.fillStyle = '#bfe4f2';
    c.textAlign = 'left';
    c.textBaseline = 'alphabetic';
    c.font = 'bold 34px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.fillText('VR 校准', 28, 60);

    // 状态
    c.fillStyle = '#9fb6c6';
    c.font = '24px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.fillText(`姿势 ${s.posture}   语音 ${s.voice}`, 28, 104);
    c.fillText(`房间 ${s.roomCode}   ${s.net}`, 28, 140);
    c.fillStyle = '#6f8ea3';
    c.font = '20px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.fillText('B/Y 关闭面板 · 扳机点按钮', 28, 176);

    // 校准行
    const rows: Array<{ label: string; value: string }> = [
      { label: '身高', value: s.scale.toFixed(2) },
      { label: '手臂伸展', value: s.reach.toFixed(2) },
      { label: '手腕旋转', value: `${Math.round(s.wristDeg)}°` },
      { label: '手腕偏转', value: `${Math.round(s.tiltDeg)}°` },
      { label: '手腕摆向', value: `${Math.round(s.yawDeg)}°` },
    ];
    rows.forEach((row, i) => {
      const y = 190 + i * 56;
      c.fillStyle = '#dde7ef';
      c.font = '26px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
      c.textAlign = 'left';
      c.textBaseline = 'alphabetic';
      c.fillText(row.label, 28, y + 30);
      c.fillStyle = '#bfe4f2';
      c.fillText(row.value, 210, y + 30);

      this.drawButton(368, y, 60, 44, '−', i * 2);
      this.drawButton(436, y, 60, 44, '+', i * 2 + 1);
    });

    // 底部按钮
    // 快捷聊天按钮
    QUICK_CHATS.forEach((text, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const index = this.regions.findIndex((r) => r.id === `chat-${i}`);
      this.drawButton(24 + col * 236, 560 + row * 52, 228, 44, text, index);
    });
    this.drawButton(24, 668, 464, 44, '打字聊天（弹系统键盘）', this.regions.findIndex((r) => r.id === 'type'));
    this.drawButton(24, 724, 464, 44, '复制邀请链接', this.regions.findIndex((r) => r.id === 'invite'));

    const bottomLabels = [
      `${s.posture === '坐' ? '起身' : '坐下'}`,
      '静音 / 开麦',
      s.bgmOcean ? '海浪：开' : '海浪：关',
      s.bgmMusic ? '音乐：开' : '音乐：关',
      s.mirrorOn ? '关闭镜子' : '打开镜子',
      '重置校准',
      s.voiceOn ? '语音：通话中（点击关闭）' : '语音：关（点击开启）',
    ];
    const bottomIds = ['sit', 'mute', 'ocean', 'music', 'mirror', 'reset', 'voice'];
    bottomLabels.forEach((label, i) => {
      const index = this.regions.findIndex((r) => r.id === bottomIds[i]);
      this.drawButton(24, 780 + i * 52, 464, 44, label, index);
    });

    c.textAlign = 'left';
    c.textBaseline = 'alphabetic';
    this.texture.needsUpdate = true;
  }

  /** 射线命中：UV → 画布坐标 → 判断按钮 */
  handleHit(uv: { x: number; y: number } | null, pressed: boolean): void {
    if (!uv) {
      if (this.hovered !== -1) {
        this.hovered = -1;
        this.redraw();
      }
      return;
    }

    const px = uv.x * CANVAS_W;
    const py = (1 - uv.y) * CANVAS_H;
    const index = this.regions.findIndex(({ rect }) => {
      const [x, y, w, h] = rect;
      return px >= x && px <= x + w && py >= y && py <= y + h;
    });

    if (index !== this.hovered) {
      this.hovered = index;
      this.redraw();
    }

    if (pressed && index >= 0) {
      this.regions[index]?.run();
    }
  }

  update(status: VrPanelStatus): void {
    this.status = status;
    this.redraw();
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }
}
